import { useToggleKeepFillingMutation } from '@/apis/idea/idea-api.api'
import { selectTheme, selectThemeStyle } from '@/features/setting/setting.slice'
import { selectUser } from '@/features/user/user.slice'
import { useAppSelector } from '@/redux/hooks'
import { ROLE_ADMIN, ROLE_MASTER } from '@/router/roles'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { UnifiedIdeaRow } from './idea-list-ssrm-adapter'
import { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise'
import { CopyOutlined, EditOutlined } from '@ant-design/icons'
import App from 'antd/es/app/index'
import { marketDateTimezoneFormatter } from '@/utils/formatters'
import { Theme } from '@/features/setting/setting.def'
import LiquidateIcon from '@/assets/icons/idea-list/liquidate.svg'
import LiquidateDarkIcon from '@/assets/icons/idea-list/liquidate-dark.svg'
import AutofillIcon from '@/assets/icons/idea-list/autofill.svg'
import AutofillDarkIcon from '@/assets/icons/idea-list/autofill-dark.svg'
import ReactDOMServer from 'react-dom/server'
import { blockedFirmIdsCreateEditIdea } from '@/utils/permissions'

interface IdeaMenuAction {
  key: string
  name: string
  visible: boolean
  disabled?: boolean
  icon: React.ReactNode
  cssClasses?: string[]
  execute: () => void | Promise<void>
}

const toMenuItem = ({
  name,
  disabled,
  icon,
  execute,
  cssClasses,
}: IdeaMenuAction): MenuItemDef => ({
  name,
  disabled,
  icon: ReactDOMServer.renderToString(icon),
  action: execute,
  cssClasses,
})

interface UseIdeaContextMenuItemsOptions {
  onEditIdea: (id: string) => void
  onLiquidateIdea: (id: string) => void
  onRefresh: () => void
}

export const useIdeaContextMenuItems = ({
  onEditIdea,
  onLiquidateIdea,
  onRefresh,
}: UseIdeaContextMenuItemsOptions) => {
  const { t } = useTranslation()
  const theme = useAppSelector(selectTheme)
  const themeStyle = useAppSelector(selectThemeStyle)
  const userInfo = useAppSelector(selectUser)
  const [toggleKeepFilling] = useToggleKeepFillingMutation()

  const isAdmin = userInfo.role === ROLE_ADMIN || userInfo.role === ROLE_MASTER
  const isBlockedFirm = blockedFirmIdsCreateEditIdea.includes(userInfo.firmId)

  const { notification } = App.useApp()

  return useCallback(
    (params: GetContextMenuItemsParams): MenuItemDef[] => {
      const idea = params.node?.data as UnifiedIdeaRow | undefined

      if (!idea) return []

      const parentIdea =
        idea.rowType === 'order'
          ? (params.node?.parent?.data as UnifiedIdeaRow | undefined)
          : idea
      const canEdit = idea.rowType === 'idea' && idea.canEdit === true
      const canToggleAutoFill =
        parentIdea?.canEdit === true && idea.state === 'Filling' && !isAdmin

      const actions: IdeaMenuAction[] = [
        {
          key: 'edit',
          name: t('idea_list.idea_table.edit'),
          visible: canEdit && !isBlockedFirm,
          icon: <EditOutlined style={{ color: themeStyle.colorText }} />,
          execute: () => onEditIdea(idea.id),
        },
        {
          key: 'liquidate',
          name: t('idea_list.idea_table.liquidate'),
          visible: canEdit && !isBlockedFirm,
          cssClasses: ['idea-context-menu__item--danger'],
          icon:
            theme === Theme.Dark ? <LiquidateDarkIcon /> : <LiquidateIcon />,
          execute: () => onLiquidateIdea(idea.id),
        },
        {
          key: 'toggle-auto-fill',
          name: parentIdea?.auto_fill
            ? t('idea_list.idea_table.disable_auto_fill')
            : t('idea_list.idea_table.enable_auto_fill'),
          visible: idea.rowType === 'order' && !isBlockedFirm,
          disabled: !canToggleAutoFill,
          icon: theme === Theme.Dark ? <AutofillDarkIcon /> : <AutofillIcon />,
          execute: async () => {
            await toggleKeepFilling({ id: idea.id }).unwrap()
            onRefresh()
          },
        },
        {
          key: 'copy-order-info',
          name: t('idea_list.idea_table.copy_order_id'),
          visible: idea.rowType === 'order',
          icon: <CopyOutlined style={{ color: themeStyle.colorText }} />,
          execute: () => {
            // SSRM does not populate the group node's childrenAfterGroup, so
            // collect the order rows for this idea by scanning all loaded nodes.
            const ideaId = parentIdea?.id
            const orderRows: UnifiedIdeaRow[] = []
            params.api.forEachNode((node) => {
              const row = node.data as UnifiedIdeaRow | undefined
              if (
                row?.rowType === 'order' &&
                row.id === ideaId &&
                !!row.orderId
              ) {
                orderRows.push(row)
              }
            })

            if (orderRows.length === 0) {
              notification.warning({
                message: t('idea_list.idea_table.copy_order_info_empty'),
                description: '',
              })
              return
            }

            const header = ['Create Time', 'Order ID'].join('\t')
            const lines = orderRows.map((row) =>
              [
                marketDateTimezoneFormatter(
                  {
                    value:
                      typeof row.createdAt === 'string' ? row.createdAt : null,
                    data: { marketTimeZone: row.marketTimeZone ?? '' },
                  },
                  { showTime: true, showTimezone: true }
                ),
                row.orderId ?? '',
              ].join('\t')
            )
            const text = [header, ...lines].join('\n')

            navigator.clipboard.writeText(text).then(
              () =>
                notification.info({
                  message: t('idea_list.idea_table.copy_order_info_success'),
                  description: '',
                }),
              () =>
                notification.error({
                  message: t('idea_list.idea_table.copy_order_info_failed'),
                  description: '',
                })
            )
          },
        },
      ]

      return actions.filter((action) => action.visible).map(toMenuItem)
    },
    [
      isAdmin,
      onEditIdea,
      onLiquidateIdea,
      onRefresh,
      t,
      theme,
      themeStyle.colorText,
      toggleKeepFilling,
      isBlockedFirm,
      notification,
    ]
  )
}
